import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../api/Api';
import initialState from '../constants/initialState';

// Get menu type / business type
export const getMenuType = createAsyncThunk('venue_register/menu_type', async (vertical, thunkAPI) => {
    try {
        const path = vertical ? `menu-type?vertical=${vertical}` : 'menu-type';
        const result = await API.getData(path);
        const response = result.data;
        if (response.status) {
            return response.data;
        } else {
            return thunkAPI.rejectWithValue({ error: response.message });
        }
    } catch (e) {
        return thunkAPI.rejectWithValue({ error: e.message });
    }
});
// Get Country data from database
export const getCountries = createAsyncThunk(
    'venue_register/country',
    async (thunkAPI) => {
        try {
            const result = await API.getData('country');
            const response = result.data;
            if (response.status) {
                return response.data;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);
// Get states by country from database
export const getStatesByCountry = createAsyncThunk(
    'venue_register/country/states',
    async (reqData, thunkAPI) => {
        try {
            const result = await API.getData('states/country', reqData);
            const response = result.data;
            if (response.status) {
                return response.data;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message })
        }
    }
);
// Get Cities by state from the database
export const getCitiesByState = createAsyncThunk(
    'venue_register/states/cities',
    async (reqData, thunkAPI) => {
        try {
            const result = await API.getData('cities/state', reqData);
            const response = result.data;
            if (response.status) {
                return response.data;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);
// Search cities by state + name prefix (typeahead — min 2 chars, max 20 results)
export const searchCities = createAsyncThunk(
    'venue_register/cities/search',
    async ({ state_id, q }, thunkAPI) => {
        try {
            const result = await API.getData('cities/search', { state_id, q });
            const response = result.data;
            if (response.status) {
                return response.data;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);
// Save data request into database
export const registrationComplete = createAsyncThunk(
    'venue_register/process',
    async (reqData, thunkAPI) => {
        try {
            const result = await API.postData('venues/register', reqData);
            const response = result.data;
            if (response.status) {
                return response.data;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

const RegisterReducer = createSlice({
    name: "venue_register",
    initialState: initialState.venue_register,
    reducers: {
        clearState(state) {
            Object.assign(state, initialState.venue_register);
        },
        registerStepVertical(state, action) {
            return {
                ...state,
                step: action.payload.step + 1,
                stepTitle: 'Create your account',
                formData: { ...state.formData, vertical: action.payload.vertical },
            };
        },
        registerStepOne(state, action) {
            return {
                ...state,
                step: action.payload.step + 1,
                stepTitle: 'About your business',
                formData: {
                    ...state.formData,
                    email: action.payload.email,
                    password: action.payload.password,
                    confirm_password: action.payload.confirm_password
                }
            }
        },
        registerStepTwo(state, action) {
            return {
                ...state,
                step: action.payload.step + 1,
                stepTitle: 'How you run it',
                formData: {
                    ...state.formData,
                    business_name: action.payload.business_name,
                    business_type: parseInt(action.payload.business_type),
                    country_id: parseInt(action.payload.country_id),
                    state_id: parseInt(action.payload.state_id),
                    city_id: parseInt(action.payload.city_id),
                }
            }
        },
        registerStepThree(state, action) {
            return {
                ...state,
                step: action.payload.step + 1,
                stepTitle: 'Build your menu',
                formData: {
                    ...state.formData,
                    signup_intent: action.payload.signup_intent || '',
                }
            }
        },
        registerStepFour(state, action) {
            return {
                ...state,
                step: action.payload.step,
                stepTitle: 'Finished',
                formData: {
                    ...state.formData,
                    menu_type: parseInt(action.payload.menu_type)
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Start - get menu type / business type
            .addCase(getMenuType.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getMenuType.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.businessType = payload;
            })
            .addCase(getMenuType.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || payload?.message || 'Something went wrong.';
            })
            //Start - get countries
            .addCase(getCountries.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getCountries.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.countries = payload;
            })
            .addCase(getCountries.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || payload?.message || 'Something went wrong.';
            })
            // Start - Get states by country
            .addCase(getStatesByCountry.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getStatesByCountry.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.states = payload;
            })
            .addCase(getStatesByCountry.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || payload?.message || 'Something went wrong.';
            })
            // Start - Get cities by state
            .addCase(getCitiesByState.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getCitiesByState.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.cities = payload;
            })
            .addCase(getCitiesByState.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || payload?.message || 'Something went wrong.';
            })
            // Start - Search cities (typeahead)
            .addCase(searchCities.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(searchCities.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.citySearch = payload;
            })
            .addCase(searchCities.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.citySearch = [];
            })
            //Start - Registration process
            .addCase(registrationComplete.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(registrationComplete.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.isError = false;
                state.isSuccess = true;
            })
            .addCase(registrationComplete.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || payload?.message || 'Something went wrong.';
            });
    }
});

export const {
    clearState,
    registerStepVertical,
    registerStepOne,
    registerStepTwo,
    registerStepThree,
    registerStepFour
} = RegisterReducer.actions;
export default RegisterReducer.reducer;
